/**
 * نظام تخزين مؤقت بسيط وسريع
 * يستخدم لتحسين الأداء وتقليل عمليات القراءة من قاعدة البيانات
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class SimpleCache {
  private cache: Map<string, CacheEntry<any>>;
  private maxSize: number;

  constructor(maxSize: number = 100) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  /**
   * حفظ بيانات في التخزين المؤقت
   */
  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    // إذا وصل الحجم للحد الأقصى، احذف أقدم عنصر
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * جلب بيانات من التخزين المؤقت
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // التحقق من انتهاء الصلاحية
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * حذف عنصر من التخزين المؤقت
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * مسح جميع العناصر
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * مسح العناصر المنتهية الصلاحية
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * الحصول على حجم التخزين المؤقت
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * التحقق من وجود مفتاح
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    // التحقق من الصلاحية
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }
}

// إنشاء مثيل واحد للتخزين المؤقت
export const simpleCache = new SimpleCache(200);

// تنظيف التخزين المؤقت كل 10 دقائق
setInterval(() => {
  simpleCache.cleanup();
}, 10 * 60 * 1000);

/**
 * دالة مساعدة للتخزين المؤقت مع دالة جلب البيانات
 */
export async function cacheOrFetch<T>(
  key: string,
  fetchFn: () => Promise<T>,
  ttl: number = 5 * 60 * 1000
): Promise<T> {
  // محاولة الجلب من التخزين المؤقت
  const cached = simpleCache.get<T>(key);
  if (cached !== null) {
    return cached;
  }

  // جلب البيانات
  const data = await fetchFn();

  // حفظ في التخزين المؤقت
  simpleCache.set(key, data, ttl);

  return data;
}

/**
 * دالة مساعدة لإنشاء مفتاح تخزين مؤقت
 */
export function createCacheKey(...parts: (string | number | boolean | undefined)[]): string {
  return parts.filter(p => p !== undefined).join('_');
}

export default simpleCache;

